import type { UserType } from "@volgo/core/contract";
import { type ReactNode, type RefObject, useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { activeCount, type DownloadSummary } from "../downloads/useDownloadSummary";
import { MyLibrary } from "../personal/MyLibrary";
import { Avatar } from "./Avatar";
import { Icon, type IconName } from "./icons";
import { ROLE_LABELS } from "./labels";
import { readPref, writePref } from "./prefs";

/** At this width and up the sidebar sits beside the content and its state is kept; below it, it overlays. */
const DOCKED = "(min-width: 1024px)";
const MOBILE = "(max-width: 640px)";

/** Whether this address shows search results (the list, or the list with a gallery window over it). */
export function isBrowsePath(pathname: string): boolean {
  return pathname === "/" || pathname.startsWith("/g/");
}

export function useMedia(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [query]);
  return matches;
}

function typing(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (target.closest("input, select, textarea, dialog") !== null || target.isContentEditable)
  );
}

/**
 * Open (panel) or closed (rail). Docked: kept per device in `volgo.sidebar`; without a saved value it starts
 * open from 1280px. Below 1024px the panel is an overlay that always starts closed and is never saved.
 */
export function useSidebar(watching = false) {
  const docked = useMedia(DOCKED);
  const [saved, setSaved] = useState(() => {
    const value = readPref("volgo.sidebar");
    return value === "open" || value === "closed"
      ? value === "open"
      : window.matchMedia("(min-width: 1280px)").matches;
  });
  const [overlay, setOverlay] = useState(false);
  const [watchOpen, setWatchOpen] = useState(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: entering/leaving watch resets its temporary choice
  useEffect(() => setWatchOpen(false), [watching]);
  const open = docked ? (watching ? watchOpen : saved) : overlay;
  const nav = useRef<HTMLElement>(null);
  const toggleButton = useRef<HTMLButtonElement>(null);
  const refocus = useRef(false);

  const toggle = useCallback(() => {
    // the button that had focus is replaced by the other state's button: focus follows it
    refocus.current = nav.current?.contains(document.activeElement) ?? false;
    if (docked && watching) setWatchOpen((was) => !was);
    else if (docked)
      setSaved((was) => {
        writePref("volgo.sidebar", was ? "closed" : "open");
        return !was;
      });
    else setOverlay((was) => !was);
  }, [docked, watching]);
  const close = useCallback(() => {
    refocus.current = nav.current?.contains(document.activeElement) ?? false;
    setOverlay(false);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: runs after each open/closed switch
  useEffect(() => {
    if (!refocus.current) return;
    refocus.current = false;
    toggleButton.current?.focus();
  }, [open]);

  // "[" switches it, unless something is being typed or a window is open (the same rule as "/")
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "[" || e.ctrlKey || e.metaKey || e.altKey) return;
      if (typing(e.target) || document.querySelector("dialog[open]")) return;
      if (window.matchMedia(MOBILE).matches) return;
      e.preventDefault();
      toggle();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [toggle]);

  // the overlay closes on Escape and when a link moves on
  const location = useLocation();
  // biome-ignore lint/correctness/useExhaustiveDependencies: closes on every move, the key is the trigger
  useEffect(() => setOverlay(false), [location.key]);
  useEffect(() => {
    if (!overlay || docked) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || document.querySelector("dialog[open]")) return;
      e.preventDefault();
      close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [overlay, docked, close]);

  return { open, overlaid: open && !docked, toggle, close, nav, toggleButton };
}
export type SidebarState = ReturnType<typeof useSidebar>;

/** Which menu entry is the current place. */
export function currentPlace(pathname: string) {
  const under = (path: string) => pathname === path || pathname.startsWith(`${path}/`);
  return {
    browse: isBrowsePath(pathname),
    videos: under("/videos"),
    bookmarks: under("/bookmarks") || pathname === "/favorites",
    downloads: under("/downloads"),
    history: under("/history"),
    settings: under("/settings"),
    admin: under("/admin"),
  };
}

/** "다운로드, 받는 중·대기 2개": the badge's number in the link's name, since the badge itself is hidden. */
export const countLabel = (label: string, count: number) =>
  count > 0 ? `${label}, 받는 중·대기 ${count}개` : undefined;

export function CountBadge({ count }: { count: number }) {
  return count > 0 ? (
    <b className="cnt" aria-hidden="true">
      {count}
    </b>
  ) : null;
}

function ToggleButton({ state, className }: { state: SidebarState; className: string }) {
  const name = state.open ? "사이드바 접기" : "사이드바 펼치기";
  return (
    <button
      ref={state.toggleButton as RefObject<HTMLButtonElement>}
      type="button"
      className={className}
      aria-expanded={state.open}
      aria-controls="sidebar"
      aria-label={name}
      title={`${name} ( [ )`}
      onClick={state.toggle}
    >
      <Icon name="side" small />
    </button>
  );
}

function RailItem({
  to,
  icon,
  label,
  current,
  count = 0,
}: {
  to: string;
  icon: IconName;
  label: string;
  current: boolean;
  /** a number badge; 0 draws none */
  count?: number;
}) {
  return (
    <Link
      className="ri"
      to={to}
      aria-current={current ? "page" : undefined}
      aria-label={countLabel(label, count)}
    >
      <span className="pill">
        <Icon name={icon} />
      </span>
      {label}
      <CountBadge count={count} />
    </Link>
  );
}

export function PanelItem({
  to,
  icon,
  label,
  current,
  children,
}: {
  to: string;
  icon: ReactNode;
  label: string;
  current: boolean;
  children?: ReactNode;
}) {
  return (
    <Link className="si" to={to} aria-current={current ? "page" : undefined}>
      {icon}
      <span className="si-name">{label}</span>
      {children}
    </Link>
  );
}

/** The sidebar: the rail when closed, the panel when open (46 Spec 3.1). */
export function Sidebar({
  state,
  user,
  summary,
  videoCount = 0,
}: {
  state: SidebarState;
  user: UserType;
  summary: DownloadSummary | null;
  videoCount?: number;
}) {
  const { pathname } = useLocation();
  const at = currentPlace(pathname);
  const admin = user.role === "admin";
  return (
    <nav id="sidebar" ref={state.nav} className="side" aria-label="주 메뉴">
      {state.open ? (
        <div className="pn">
          <div className="pn-top">
            <Link className="logo" to="/">
              Volgo
            </Link>
            <ToggleButton state={state} className="iconbtn" />
          </div>
          <div className="sg">
            <PanelItem to="/" icon={<Icon name="compass" />} label="탐색" current={at.browse} />
            <PanelItem to="/videos" icon={<Icon name="play" />} label="영상" current={at.videos}>
              <CountBadge count={videoCount} />
            </PanelItem>
          </div>
          <MyLibrary pathname={pathname} history={at.history} downloads={at.downloads} summary={summary} />
          <div className="me">
            <Avatar user={user} />
            <div className="me-name">
              <b>{user.username}</b>
              <small>{ROLE_LABELS[user.role]}</small>
            </div>
            <div className="me-end">
              {admin && (
                <Link
                  className="iconbtn"
                  to="/admin"
                  aria-label="관리"
                  aria-current={at.admin ? "page" : undefined}
                >
                  <Icon name="shield" small />
                </Link>
              )}
              <Link
                className="iconbtn"
                to="/settings"
                aria-label="설정"
                aria-current={at.settings ? "page" : undefined}
              >
                <Icon name="gear" small />
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <div className="rn">
          <ToggleButton state={state} className="tog-s" />
          <RailItem to="/" icon="compass" label="탐색" current={at.browse} />
          <RailItem to="/videos" icon="play" label="영상" current={at.videos} count={videoCount} />
          <RailItem to="/bookmarks" icon="bookmark" label="북마크" current={at.bookmarks} />
          <div className="rsep" />
          <RailItem
            to="/downloads"
            icon="down"
            label="다운로드"
            current={at.downloads}
            count={activeCount(summary)}
          />
          <RailItem to="/history" icon="clock" label="기록" current={at.history} />
          <div className="rend">
            {admin && <RailItem to="/admin" icon="shield" label="관리" current={at.admin} />}
            <RailItem to="/settings" icon="gear" label="설정" current={at.settings} />
            <span className="rail-me" role="img" aria-label={user.username} title={user.username}>
              <Avatar user={user} />
            </span>
          </div>
        </div>
      )}
    </nav>
  );
}
